import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ServiceIntervalSection } from './ServiceIntervalSection';

/**
 * Non-fuel cost details: mandatory odometer/hours reading, supplier/vendor,
 * invoice number, and an embedded ServiceIntervalSection when cost_type is 'service'.
 */
export const NonFuelDetailsSection = ({
  formData,
  updateField,
  setFormData,
  isHoursBased,
  isMeterExempt,
  lastVehicleOdo,
  suppliers,
  getRelevantSuppliers,
  serviceConfirmed,
  setServiceConfirmed,
  nextServiceOdo,
  setNextServiceOdo,
  expectedNextServiceDate,
  avgKmPerDay,
  allVehicleCosts,
  selectedVehicle,
}) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label>
        {isHoursBased
          ? (isMeterExempt ? 'Hours Reading (optional)' : 'Hours Reading *')
          : (isMeterExempt ? 'Odometer Reading (km) — optional' : 'Odometer Reading (km) *')}
      </Label>
      {isHoursBased ? (
        <Input
          type="number"
          step="0.1"
          min="0"
          value={formData.hours}
          onChange={(e) => updateField('hours', e.target.value)}
          placeholder="Current hours reading"
          className="font-data"
          data-testid="cost-hours-input"
          required={!isMeterExempt}
        />
      ) : (
        <Input
          type="number"
          step="1"
          min="0"
          value={formData.odometer}
          onChange={(e) => updateField('odometer', e.target.value)}
          placeholder="Current odometer reading"
          className="font-data"
          data-testid="cost-odometer-input"
          required={!isMeterExempt}
        />
      )}
      {isMeterExempt && (
        <p className="text-xs text-slate-600 italic" data-testid="meter-exempt-hint-nonfuel">
          This vehicle is excluded from the fleet average — no meter reading needed.
        </p>
      )}
      {!isHoursBased && lastVehicleOdo?.odometer && (
        <p className="text-xs text-red-600 font-medium" data-testid="prev-odo-nonfuel">
          Previous Odo: {lastVehicleOdo.odometer.toLocaleString()} km ({lastVehicleOdo.date})
          {lastVehicleOdo.liters ? ` | ${lastVehicleOdo.liters}L` : ''}
          {lastVehicleOdo.amount ? ` | R ${Number(lastVehicleOdo.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}
        </p>
      )}
      {!isHoursBased && lastVehicleOdo?.odometer && formData.odometer && parseFloat(formData.odometer) < lastVehicleOdo.odometer && (
        <div className="p-2 bg-red-100 border border-red-400 rounded text-xs text-red-800 font-bold" data-testid="nonfuel-odo-warning">
          Odometer {parseFloat(formData.odometer).toLocaleString()} km is LOWER than last recorded {lastVehicleOdo.odometer.toLocaleString()} km — please verify
        </div>
      )}
      {isHoursBased && lastVehicleOdo?.hours && (
        <p className="text-xs text-red-600 font-medium" data-testid="prev-hours-nonfuel">
          Previous Hours: {lastVehicleOdo.hours.toLocaleString()} hrs ({lastVehicleOdo.date})
          {lastVehicleOdo.liters ? ` | ${lastVehicleOdo.liters}L` : ''}
          {lastVehicleOdo.amount ? ` | R ${Number(lastVehicleOdo.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}
        </p>
      )}
    </div>

    {formData.cost_type === 'service' && (
      <ServiceIntervalSection
        formData={formData}
        updateField={updateField}
        isHoursBased={isHoursBased}
        serviceConfirmed={serviceConfirmed}
        setServiceConfirmed={setServiceConfirmed}
        nextServiceOdo={nextServiceOdo}
        setNextServiceOdo={setNextServiceOdo}
        expectedNextServiceDate={expectedNextServiceDate}
        avgKmPerDay={avgKmPerDay}
        allVehicleCosts={allVehicleCosts}
        selectedVehicle={selectedVehicle}
      />
    )}

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Supplier</Label>
        {suppliers.length > 0 ? (
          <Select
            value={formData.supplier_id}
            onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
          >
            <SelectTrigger data-testid="cost-supplier-select">
              <SelectValue placeholder="Select supplier" />
            </SelectTrigger>
            <SelectContent>
              {getRelevantSuppliers().map((supplier) => (
                <SelectItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={formData.vendor}
            onChange={(e) => updateField('vendor', e.target.value)}
            placeholder="Service provider"
            data-testid="cost-vendor-input"
          />
        )}
      </div>
      <div className="space-y-2">
        <Label>Invoice Number</Label>
        <Input
          value={formData.invoice_number}
          onChange={(e) => updateField('invoice_number', e.target.value)}
          placeholder="INV-00001"
          className="font-data"
          data-testid="cost-invoice-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Order Nr</Label>
        <Input
          value={formData.order_number || ''}
          onChange={(e) => updateField('order_number', e.target.value)}
          placeholder="PO-00001"
          className="font-data"
          data-testid="cost-order-input"
        />
      </div>
    </div>
  </div>
);
